import type { Editor } from '@tiptap/react';
import { useEffect, useReducer } from 'react';
import {
  Bold,
  Code,
  FileCode,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface Props {
  editor: Editor | null;
  readOnly: boolean;
}

export function CollaborativeEditorToolbar({ editor, readOnly }: Props): React.ReactElement | null {
  useEditorChangeSubscription(editor);
  if (editor === null || readOnly) return null;

  const can = editor.can();

  return (
    <div
      className="flex flex-wrap items-center gap-0.5 rounded-md border border-border bg-surface-elevated/40 px-1.5 py-1"
      role="toolbar"
      aria-label="Body editor toolbar"
    >
      <ToolbarButton
        icon={Heading1}
        label="Heading 1"
        shortcut="⌘⌥1"
        active={editor.isActive('heading', { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      />
      <ToolbarButton
        icon={Heading2}
        label="Heading 2"
        shortcut="⌘⌥2"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      />
      <ToolbarButton
        icon={Heading3}
        label="Heading 3"
        shortcut="⌘⌥3"
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      />

      <ToolbarSeparator />

      <ToolbarButton
        icon={Bold}
        label="Bold"
        shortcut="⌘B"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        icon={Italic}
        label="Italic"
        shortcut="⌘I"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        icon={Code}
        label="Inline code"
        shortcut="⌘E"
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      />

      <ToolbarSeparator />

      <ToolbarButton
        icon={List}
        label="Bullet list"
        shortcut="⌘⇧8"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        icon={ListOrdered}
        label="Numbered list"
        shortcut="⌘⇧7"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <ToolbarButton
        icon={Quote}
        label="Quote"
        shortcut="⌘⇧B"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />
      <ToolbarButton
        icon={FileCode}
        label="Code block"
        shortcut="⌘⌥C"
        active={editor.isActive('codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      />

      <ToolbarSeparator />

      <ToolbarButton
        icon={Undo2}
        label="Undo"
        shortcut="⌘Z"
        disabled={!can.undo()}
        onClick={() => editor.chain().focus().undo().run()}
      />
      <ToolbarButton
        icon={Redo2}
        label="Redo"
        shortcut="⌘⇧Z"
        disabled={!can.redo()}
        onClick={() => editor.chain().focus().redo().run()}
      />
    </div>
  );
}

interface ToolbarButtonProps {
  icon: LucideIcon;
  label: string;
  shortcut?: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function ToolbarButton({
  icon: Icon,
  label,
  shortcut,
  active = false,
  disabled = false,
  onClick,
}: ToolbarButtonProps): React.ReactElement {
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      aria-label={label}
      title={shortcut !== undefined ? `${label} (${shortcut})` : label}
      className={cn('h-7 w-7 p-0', active && 'bg-accent text-accent-foreground hover:bg-accent/90')}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </Button>
  );
}

function ToolbarSeparator(): React.ReactElement {
  return <Separator orientation="vertical" className="mx-1 h-5" />;
}

/**
 * Re-render the toolbar whenever the editor's selection or content changes
 * so isActive() / can() reflect the current state. Tiptap mutates the editor
 * instance in place, so React would otherwise miss the change.
 */
function useEditorChangeSubscription(editor: Editor | null): void {
  const [, force] = useReducer((tick: number) => tick + 1, 0);
  useEffect(() => {
    if (editor === null) return;
    const handler = (): void => {
      force();
    };
    editor.on('transaction', handler);
    editor.on('selectionUpdate', handler);
    return (): void => {
      editor.off('transaction', handler);
      editor.off('selectionUpdate', handler);
    };
  }, [editor]);
}
